import { useState } from 'react'

/**
 * Convert an ISO standard date into a local date time string
 * @param isoDate ISO Formatted date
 * @returns
 */
export const ISODateToLocal = (
  isoDate,
  options?: {
    format?: 'date' | 'time'
  }
) => {
  const date = new Date(isoDate)

  if (isNaN(date.getTime())) return isoDate

  if (options?.format === 'date') {
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    })
  }
  if (options?.format === 'time') {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
  }

  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Get today's date in the
 * local timezone as yyyy-mm-dd
 */
const get_date = () => {
  let today = new Date()
  const offset = today.getTimezoneOffset()
  today = new Date(today.getTime() - offset * 60 * 1000)
  return today.toISOString().split('T')[0]
}

/**
 * Get data about the current date and time
 * @returns
 */
export const getDateTimeData = () => {
  return {
    date: get_date(),
    now: new Date().toISOString(),
  }
}

/**
 * Convert an ISO date string to a local date string
 * @param dateString ISO formatted date string e.g. "2023-10-01T12:00:00Z"
 * @returns Local User formatted date string e.g. "10/01/2023 12:00 PM"
 */
export const convertDateStringToLocal = (dateString: string) => {
  if (!dateString) {
    return 'Unknown'
  }

  const date = new Date(dateString)
  const formattedDate = date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  const formattedTime = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })
  return `${formattedDate} ${formattedTime}`
}

/**
 * Get an object containing the headings from a 2D array
 * @param data 2d array of data with column headings as the first row
 * @returns Objects with headings as 'selection_n'
 */
export const getHeadingsFromArray = (data: any[][]) => {
  const variables = {}
  variables['selection'] = data[0][0]

  // Add each column heading as a variable
  for (let i = 0; i < data[0].length; i++) {
    variables[`selection_${i}`] = data[0][i]
  }
  return variables
}

/**
 * Determine how to handle recipe responses
 * @param results data response from executeRecipe fetch function
 * @returns Right now an array of data for excel
 */
export const recipeDataHandler = (results) => {
  //Check if response is array if true then get first elements
  if (Array.isArray(results)) {
    results = results[0]
  }

  //Check if the object contains "columns" key if true then append the columns to the first position of data array
  if (Object.keys(results).includes('columns')) {
    var cols = results['columns']
    results = results['data']
    results.unshift(cols)
  } else {
    results = results['data']
  }

  return results
}

/**
 * Reformat array outputs for Stock Extract Wrangles
 * @param data Array of results. Each element can be an array or object
 * @param arrayFormat Format of the result where each row is an array
 *   - first: return only the first element of each array
 *   - delimited: return each array joined by a delimiter set by arrayDelimiter
 *   - json: return each array as a JSON string
 * @param arrayDelimiter Delimiter to use when arrayFormat is 'delimited'
 * @returns
 */
export const formatExtractOutputs = (
  data: any[],
  arrayFormat: string,
  arrayDelimiter: string
) => {
  return data.map((row: any) => {
    if (Array.isArray(row)) {
      if (arrayFormat === 'first' && row.length > 0) {
        return [row[0]]
      }
      if (arrayFormat === 'delimited') {
        return [row.join(arrayDelimiter)]
      }
      if (arrayFormat === 'json') {
        return [JSON.stringify(row)]
      }
      return row
    }
    return [row]
  })
}

/**
 * Merge two arrays into an object with keys from the first array and values from the second array
 * @param keys Array of keys
 * @param values Array of values
 * @returns Object with keys and values merged
 */
function mergeArraysToObject(
  keys: any[],
  values: any[]
): { [key: string]: any } {
  const result: { [key: string]: any } = {}
  for (let i = 0; i < keys.length; i++) {
    result[keys[i]] = values[i]
  }
  return result
}

/**
 * Reformat array and write back to range (wrangle data)
 * @param results data response from executeRecipe fetch function
 * @param useLabels boolean value for whether to use labels
 * @param arrayFormat string value for how to format arrays
 * @param arrayDelimiter string value for how to delimit arrays
 * @param columns array of column names
 */
export const wrangleOutputHandler = (
  results: any[],
  useLabels: string,
  arrayFormat: string,
  arrayDelimiter: string,
  columns?: string[]
) => {
  var output = []
  for (var x of results) {
    if (Array.isArray(x) && arrayFormat === 'delimited') {
      output.push([x.join(arrayDelimiter)])
    } else if (
      Array.isArray(x) &&
      arrayFormat === 'first' &&
      useLabels !== 'True'
    ) {
      output.push([x[0]])
    } else if (
      typeof x === 'object' &&
      arrayFormat === 'first' &&
      useLabels === 'True'
    ) {
      if (Object.keys(x).length != 0) {
        const newObject = {}
        for (const key in x) {
          if (x.hasOwnProperty(key) && Array.isArray(x[key])) {
            newObject[key] = x[key][0]
          }
        }
        output.push([JSON.stringify(newObject)])
      } else {
        output.push([''])
      }
    } else if (
      typeof x === 'object' &&
      useLabels === 'True' &&
      arrayFormat === 'delimited'
    ) {
      if (Object.keys(x).length != 0) {
        const newObject = {}
        for (const key in x) {
          if (x.hasOwnProperty(key) && Array.isArray(x[key])) {
            newObject[key] = x[key].join(arrayDelimiter)
          }
        }
        output.push([JSON.stringify(newObject)])
      } else {
        output.push([''])
      }
    } else if (typeof x == 'string') {
      output.push([x])
    } else if (Array.isArray(x) && arrayFormat != 'json') {
      // Ensure array contains something
      // else excel will throw an error
      if (x.length == 0) {
        output.push([''])
      } else {
        output.push(x)
      }
    } else if (
      Array.isArray(x) &&
      arrayFormat === 'json' &&
      useLabels == 'True' &&
      columns
    ) {
      // If dataframe format, merge to [{column: value}]
      output.push([JSON.stringify(mergeArraysToObject(columns, x))])
    } else {
      output.push([JSON.stringify(x)])
    }
  }
  return output
}

/**
 * Normalize an array of arrays so that each sub-array is the same length
 * based on the maximum length of the sub-arrays
 * @param arr Array to be normalized
 * @returns
 */
export function normalizeArrays(arr: string[][]): string[][] {
  // Find the maximum length of the sub-arrays
  const maxLength = Math.max(...arr.map((subArr) => subArr.length))

  // Iterate over each sub-array
  for (let subArr of arr) {
    // Add empty strings until it is the same length as the maximum length
    while (subArr.length < maxLength) {
      subArr.push('')
    }
  }

  return arr
}

/**
 * Parse a string that could be either a comma delimited list a,b,c
 * or a JSON array ["a","b","c"] into an array
 */
export const parseDelimitedOrJSONString = (value: string): any[] => {
  try {
    if (value[0] === '[') {
      return Array.from(new Set(JSON.parse(value)))
    } else {
      // dropdown value is string with commas
      return Array.from(new Set(value.split(',').map((x) => x.trim())))
    }
  } catch {
    return undefined
  }
}

export const dateToYYYYMMDD = (date: Date) => {
  date = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  return date.toISOString().split('T')[0]
}

/**
 * Convert a string To Title Case
 * @param str Input string
 * @returns String in title case
 */
export function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  })
}

/**
 * Convert a string to JSON object if possible
 * @param value Input value
 * @returns Value as a JSON object or the original value
 */
export const tryParseJSON = (value: any) => {
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

/**
 * Custom hook to use retrieve and save values to local storage
 * @param key localStorage key
 * @param defaultValue default value if key is not found
 */
export function useLocalStorage(key, defaultValue) {
  let initialValue
  try {
    // Get initial value from local storage or use default value
    initialValue = JSON.parse(localStorage.getItem(key)) || defaultValue
  } catch (error) {
    console.error(`Error reading localStorage key “${key}”:`, error)
    initialValue = defaultValue
  }

  // State to hold the value
  const [value, setValue] = useState(initialValue)

  // Save the value to local storage whenever it changes
  const saveValueToLocalStorage = (newValue) => {
    try {
      setValue(newValue)
      localStorage.setItem(key, JSON.stringify(newValue))
    } catch (error) {
      console.error(`Error setting localStorage key “${key}”:`, error)
    }
  }

  return [value, saveValueToLocalStorage]
}

/**
 * Take a 2D array and convert it to a DataFrame-like object
 * @param array 2D array of data with column headings as the first row
 * @returns Object with keys as column headings and values as arrays of data - DataFrame
 */
export function arrayToDataFrame(array: any[][]): { [key: string]: any[] } {
  const [headers, ...rows] = array // Destructure into first row (headers) and the rest (data)
  const dataframe: { [key: string]: any[] } = {}

  // Initialize empty arrays for each header
  headers.forEach((header) => {
    dataframe[header] = []
  })

  // Fill the arrays
  rows.forEach((row) => {
    row.forEach((cell, colIndex) => {
      const header = headers[colIndex]
      dataframe[header].push(cell)
    })
  })

  return dataframe
}

export const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = [
    'Bytes',
    'KiB',
    'MiB',
    'GiB',
    'TiB',
    'PiB',
    'EiB',
    'ZiB',
    'YiB',
  ]

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}
